import { useState, useEffect, useCallback } from 'react';
import {
  IconAdjustmentsFilled,
  IconCategoryFilled,
  IconDiscountFilled,
  IconPencilFilled,
  IconTagFilled,
} from '@tabler/icons-react';
import categoryIcons from '../Utils/categoryIcons';
import ModalAgregarVariante from '../components/ModalAgregarVariante';
import ModalAgregarCategoria from '../components/ModalAgregarCategoria';
import ModalDescuento from '../components/ModalDescuento';
import ModalEditarVariante from '../components/ModalEditarVariante';
import ModalEditarCategoria from '../components/ModalEditarCategoria';
import { apiFetch } from '../api/client';
import TipBlue from '../Utils/TipBlue';

const FONT = '"Public Sans", sans-serif';

const PROMO_PALETTE = ['#22D3EE', '#A78BFA', '#34D399', '#F97316', '#F472B6', '#60A5FA'];

function normalizeSaleTypes(value) {
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = null;
    }
  }
  return Array.isArray(parsed) && parsed.length ? parsed : ['in_store', 'pickup', 'delivery'];
}

function mapCategoryDiscount(d, i) {
  return {
    kind: 'category',
    id: d.id,
    name: d.name,
    description: d.description,
    discount_type: d.discount_type,
    discount_value: Number(d.discount_value),
    min_quantity: d.min_quantity,
    applies_to_sale_types: normalizeSaleTypes(d.applies_to_sale_types),
    start_date: d.start_date,
    end_date: d.end_date,
    categories: d.categories || [],
    color: PROMO_PALETTE[i % PROMO_PALETTE.length],
  };
}

function mapIndividualDiscount(d, i) {
  return {
    kind: 'individual',
    id: d.id,
    name: d.name,
    description: d.description,
    item_id: d.item_id,
    old_price: d.old_price != null ? Number(d.old_price) : null,
    new_price: d.new_price != null ? Number(d.new_price) : null,
    applies_to_sale_types: normalizeSaleTypes(d.applies_to_sale_types),
    start_date: d.start_date,
    end_date: d.end_date,
    color: PROMO_PALETTE[i % PROMO_PALETTE.length],
  };
}

function hexToRgba(hex, alpha) {
  const h = String(hex || '#8e8e93').replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function promoMeta(promo) {
  if (promo.kind === 'individual') {
    return promo.new_price != null ? `Precio especial $${promo.new_price.toFixed(2)}` : 'Descuento individual';
  }
  if (promo.discount_type === 'two_for_one') return '2x1';
  if (promo.discount_type === 'percentage') return `${promo.discount_value}% de descuento`;
  return `$${promo.discount_value.toFixed(2)} de descuento`;
}

function ItemRow({ name, color, icon, glyph: Glyph, meta, onEdit }) {
  const IconComp = Glyph || (icon ? categoryIcons[icon] : null);
  const tint = color || '#8e8e93';

  return (
    <button
      type="button"
      onClick={onEdit}
      aria-label={`Editar ${name}`}
      className="flex items-center gap-3 w-full min-h-[56px] px-3 rounded-2xl border-none cursor-pointer text-left touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.995] [@media(hover:hover)]:hover:bg-white/[0.06]"
      style={{ backgroundColor: 'rgba(255, 255, 255, 0.028)', fontFamily: FONT }}
    >
      <span
        className="flex items-center justify-center shrink-0 w-9 h-9 rounded-full"
        style={{ backgroundColor: hexToRgba(tint, 0.12) }}
      >
        {IconComp ? (
          <IconComp size={18} color={tint} />
        ) : (
          <span className="rounded-full" style={{ width: '8px', height: '8px', backgroundColor: tint }} />
        )}
      </span>

      <span className="flex flex-col min-w-0 flex-1">
        <span
          className="truncate"
          style={{
            fontSize: '15px',
            lineHeight: '20px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: 'rgba(255, 255, 255, 0.9)',
          }}
        >
          {name}
        </span>
        {meta && (
          <span
            className="truncate mt-0.5"
            style={{
              fontSize: '12px',
              lineHeight: '16px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.38)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {meta}
          </span>
        )}
      </span>

      <span
        className="flex items-center justify-center shrink-0 w-8 h-8 rounded-full"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)' }}
      >
        <IconPencilFilled size={13} color="rgba(255, 255, 255, 0.45)" />
      </span>
    </button>
  );
}

function ColumnEmpty({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <span
        className="flex items-center justify-center w-11 h-11 rounded-2xl mb-1"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.035)' }}
      >
        <Icon size={22} color="rgba(255, 255, 255, 0.3)" />
      </span>
      <p
        className="m-0"
        style={{
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          color: 'rgba(255, 255, 255, 0.9)',
          fontFamily: FONT,
        }}
      >
        {title}
      </p>
      <p
        className="m-0"
        style={{
          fontSize: '13px',
          lineHeight: '19px',
          fontWeight: 500,
          color: 'rgba(255, 255, 255, 0.4)',
          maxWidth: '220px',
          fontFamily: FONT,
        }}
      >
        {hint}
      </p>
    </div>
  );
}

function ColumnSkeleton() {
  return (
    <div className="flex flex-col gap-1.5" role="status" aria-label="Cargando">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-2xl animate-pulse"
          style={{ height: '56px', padding: '0 12px', backgroundColor: 'rgba(255, 255, 255, 0.025)' }}
        >
          <span className="shrink-0 rounded-full" style={{ width: '36px', height: '36px', backgroundColor: '#141414' }} />
          <span className="rounded-full" style={{ width: '58%', height: '11px', backgroundColor: '#141414' }} />
        </div>
      ))}
    </div>
  );
}

function SectionColumn({ id, title, accent, count, onAdd, addLabel, active, children }) {
  return (
    <section
      className={`${active ? 'flex' : 'hidden'} lg:flex flex-col min-h-0 min-w-0 flex-1 lg:min-w-[280px]`}
      aria-labelledby={`seccion-${id}`}
    >
      <div className="flex items-center justify-between gap-3 px-1.5 pb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="shrink-0 rounded-full"
            style={{ width: 6, height: 6, backgroundColor: accent }}
          />
          <h2
            id={`seccion-${id}`}
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '17px',
              lineHeight: '22px',
              letterSpacing: '-0.01em',
              color: 'rgba(255, 255, 255, 0.9)',
              fontFamily: FONT,
            }}
          >
            {title}
          </h2>
          <span
            className="inline-flex items-center justify-center shrink-0 h-7 min-w-[28px] rounded-full px-2.5"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              fontSize: '12px',
              fontWeight: 600,
              color: 'rgba(255, 255, 255, 0.5)',
              fontFamily: FONT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {count}
          </span>
        </div>
        <button
          type="button"
          onClick={onAdd}
          aria-label={addLabel}
          className="flex items-center justify-center shrink-0 h-10 px-4 rounded-full border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.97] [@media(hover:hover)]:hover:bg-[#0077ed]"
          style={{
            backgroundColor: '#0071e3',
            fontSize: '13px',
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: '#fff',
            fontFamily: FONT,
          }}
        >
          Agregar
        </button>
      </div>

      <div
        className="flex flex-col gap-1.5 flex-1 min-h-0 overflow-y-auto rounded-3xl p-2 lg:p-2.5 pedidos-scroll"
        style={{ backgroundColor: 'rgba(255, 255, 255, 0.022)', overscrollBehavior: 'contain' }}
      >
        {children}
      </div>
    </section>
  );
}

export default function CategoryPromoVariants({ dynamicIslandRef }) {
  const [modalOpen, setModalOpen] = useState(null);
  const [editingVariant, setEditingVariant] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [variants, setVariants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promos, setPromos] = useState([]);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('variantes');

  const fetchData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [vRes, cRes, cdRes, idRes] = await Promise.all([
        apiFetch(`/variants/groups`),
        apiFetch(`/categories`),
        apiFetch(`/category-discounts`),
        apiFetch(`/individual-discounts`),
      ]);
      if (vRes.ok) setVariants(await vRes.json());
      if (cRes.ok) setCategories(await cRes.json());
      if (cdRes.ok && idRes.ok) {
        const cats = await cdRes.json();
        const inds = await idRes.json();
        setPromos([
          ...cats.map((d, i) => mapCategoryDiscount(d, i)),
          ...inds.map((d, i) => mapIndividualDiscount(d, cats.length + i)),
        ]);
      } else {
        setPromos([]);
      }
    } catch (e) {
      dynamicIslandRef?.current?.addAlert('error', 'Algo ha salido mal. Si el problema persiste, por favor reporta el error.');
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [dynamicIslandRef]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const sections = [
    {
      id: 'variantes',
      title: 'Variantes',
      count: variants.length,
      accent: '#0a84ff',
      icon: IconAdjustmentsFilled,
      emptyTitle: 'Sin variantes',
      emptyHint: 'Crea grupos como Tamaño o Sabor para tus productos.',
      addLabel: 'Agregar variante',
      onAdd: () => setModalOpen('variantes'),
    },
    {
      id: 'categorias',
      title: 'Categorías',
      count: categories.length,
      accent: '#30d158',
      icon: IconCategoryFilled,
      emptyTitle: 'Sin categorías',
      emptyHint: 'Agrupa tu catálogo para encontrarlo más rápido.',
      addLabel: 'Agregar categoría',
      onAdd: () => setModalOpen('categorias'),
    },
    {
      id: 'promociones',
      title: 'Promociones',
      count: promos.length,
      accent: '#f87c2b',
      icon: IconDiscountFilled,
      emptyTitle: 'Sin promociones',
      emptyHint: 'Crea descuentos por categoría o por artículo.',
      addLabel: 'Agregar promoción',
      onAdd: () => { setEditingPromo(null); setPromoModalOpen(true); },
    },
  ];

  const renderSectionBody = (section) => {
    if (loading) return <ColumnSkeleton />;

    if (section.id === 'variantes') {
      if (variants.length === 0) {
        return <ColumnEmpty icon={section.icon} title={section.emptyTitle} hint={section.emptyHint} />;
      }
      return variants.map((v) => (
        <ItemRow
          key={v.id}
          name={v.name}
          color={v.color || '#3B82F6'}
          icon={null}
          meta={`${(v.options || []).length} ${(v.options || []).length === 1 ? 'opción' : 'opciones'}${v.is_required ? ' · Obligatoria' : ''}`}
          onEdit={() => setEditingVariant(v)}
        />
      ));
    }

    if (section.id === 'categorias') {
      if (categories.length === 0) {
        return <ColumnEmpty icon={section.icon} title={section.emptyTitle} hint={section.emptyHint} />;
      }
      return categories.map((c) => (
        <ItemRow
          key={c.id}
          name={c.name}
          color={c.color || '#3B82F6'}
          icon={c.icon}
          meta={c.description || null}
          onEdit={() => setEditingCategory(c)}
        />
      ));
    }

    if (promos.length === 0) {
      return <ColumnEmpty icon={section.icon} title={section.emptyTitle} hint={section.emptyHint} />;
    }
    return promos.map((p) => (
      <ItemRow
        key={`${p.kind}-${p.id}`}
        name={p.name}
        color={p.color}
        glyph={p.kind === 'individual' ? IconTagFilled : IconDiscountFilled}
        meta={promoMeta(p)}
        onEdit={() => { setEditingPromo(p); setPromoModalOpen(true); }}
      />
    ));
  };

  return (
    <div className="flex flex-col h-screen bg-black overflow-hidden">
      <header className="shrink-0 px-4 lg:px-8 pt-5 lg:pt-6 pb-3 lg:pb-4 border-b border-white/[0.16] flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1
            className="font-semibold m-0 truncate"
            style={{
              fontSize: '22px',
              lineHeight: '28px',
              letterSpacing: '-0.02em',
              color: 'rgba(255, 255, 255, 0.95)',
              fontFamily: FONT,
            }}
          >
            Promociones por categoría
          </h1>
          <p
            className="m-0 mt-1"
            style={{
              fontSize: '13px',
              lineHeight: '18px',
              fontWeight: 500,
              color: 'rgba(255, 255, 255, 0.45)',
              fontFamily: FONT,
            }}
          >
            Administra las variantes y promociones de cada categoría
          </p>
        </div>
        <TipBlue openUp={false} alignRight={true}>Usa Agregar en cada columna para crear variantes, categorías o promociones.</TipBlue>
      </header>

      <div className="flex-1 min-h-0 flex flex-col px-4 lg:px-8 pt-5 pb-4">
        <div className="pb-4 lg:hidden">
          <div
            className="flex gap-1 p-1 rounded-2xl"
            role="tablist"
            aria-label="Secciones"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.04)' }}
          >
            {sections.map((section) => {
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center justify-center gap-2 flex-1 min-w-0 h-11 px-2 rounded-xl border-none cursor-pointer touch-manipulation select-none transition-colors duration-200 ease-out active:scale-[0.99] ${
                    isActive ? 'bg-white/[0.09]' : 'bg-transparent'
                  }`}
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    letterSpacing: '-0.01em',
                    color: isActive ? 'rgba(255, 255, 255, 0.95)' : 'rgba(255, 255, 255, 0.45)',
                    fontFamily: FONT,
                  }}
                >
                  <span className="truncate">{section.title}</span>
                  <span
                    className="inline-flex items-center justify-center shrink-0 h-5 min-w-[20px] rounded-full px-1.5"
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.07)',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'rgba(255, 255, 255, 0.55)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {section.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0" aria-busy={loading}>
          <div className="flex gap-3 lg:gap-4 h-full lg:overflow-x-auto pedidos-scroll">
            {sections.map((section) => (
              <SectionColumn
                key={section.id}
                id={section.id}
                title={section.title}
                accent={section.accent}
                count={section.count}
                onAdd={section.onAdd}
                addLabel={section.addLabel}
                active={activeSection === section.id}
              >
                {renderSectionBody(section)}
              </SectionColumn>
            ))}
          </div>
        </div>
      </div>

      <ModalAgregarVariante
        isOpen={modalOpen === 'variantes'}
        onClose={() => setModalOpen(null)}
        onCreated={() => { setModalOpen(null); fetchData(false); }}
        dynamicIslandRef={dynamicIslandRef}
      />
      <ModalAgregarCategoria
        isOpen={modalOpen === 'categorias'}
        onClose={() => setModalOpen(null)}
        onCreated={() => { setModalOpen(null); fetchData(false); }}
        dynamicIslandRef={dynamicIslandRef}
      />
      <ModalDescuento
        isOpen={promoModalOpen}
        onClose={() => { setPromoModalOpen(false); setEditingPromo(null); }}
        onSaved={() => { setPromoModalOpen(false); setEditingPromo(null); fetchData(false); }}
        promo={editingPromo}
        dynamicIslandRef={dynamicIslandRef}
      />
      <ModalEditarVariante
        isOpen={!!editingVariant}
        onClose={() => setEditingVariant(null)}
        onUpdated={() => { setEditingVariant(null); fetchData(false); }}
        variant={editingVariant}
        dynamicIslandRef={dynamicIslandRef}
      />
      <ModalEditarCategoria
        isOpen={!!editingCategory}
        onClose={() => setEditingCategory(null)}
        onUpdated={() => { setEditingCategory(null); fetchData(false); }}
        category={editingCategory}
        dynamicIslandRef={dynamicIslandRef}
      />
    </div>
  );
}
